import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTwitchChannel } from '../hooks/useTwitch';
import {
  DesktopLinks,
  LoginButton,
  UserMenu,
  MobileMenu
} from './nav/NavParts';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const { data: channel } = useTwitchChannel();
  const location = useLocation();
  const isLive = channel?.is_live;
  const isHome = location.pathname === '/';
  const avatarUrl = channel?.broadcaster?.profile_image_url;
  const displayName = channel?.broadcaster?.display_name || 'JorDievoorje';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const headerCls = scrolled
    ? 'bg-zinc-950/80 backdrop-blur-xl border-b border-white/5'
    : 'bg-zinc-950/70 backdrop-blur-xl border-b border-white/5';

  const brandBubble = (
    <div className="relative w-10 h-10">
      {avatarUrl && (
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-600 p-[2px]">
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-full h-full rounded-full object-cover"
          />
          {isLive && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-zinc-950 animate-pulse" />
          )}
        </div>
      )}
    </div>
  );

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-colors duration-300 ${headerCls}`}
      style={{ top: 'calc(var(--stats-ticker-top-h, 0px) + var(--stats-ticker-gap, 0px))' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        {isHome ? (
          <a
            href="#home"
            data-testid="nav-brand-home"
            className="flex items-center gap-3 group"
          >
            {brandBubble}
          </a>
        ) : (
          <Link
            to="/"
            data-testid="nav-brand-home"
            title="Terug naar home"
            className="flex items-center gap-3 group"
          >
            {brandBubble}
          </Link>
        )}

        <DesktopLinks />

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <UserMenu
              user={user}
              menuOpen={userMenuOpen}
              onToggle={() => setUserMenuOpen((v) => !v)}
              onLogout={async () => {
                setUserMenuOpen(false);
                await logout();
              }}
            />
          ) : (
            <LoginButton onClick={login} />
          )}
        </div>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        onLogin={login}
        onLogout={logout}
      />
    </header>
  );
}
